import { i18n } from '@shared/i18n';

import { SubpropertyService } from '@modules/subproperty/subproperty-service';
import Errors from '@shared/error/errors';
import _ from 'lodash';
import config from '@/config';

import stripePaymentFeesByCountry from '@enums/stripePaymentFeesByCountry.json';

const tLayout = {
  TEXT: 'text',
  LINK_BOX: 'link_box',
  TEXT_BOX: 'text_box',
  BUTTON: 'button',
  STATUS: 'status',
  EMPTY: 'empty',
  LINK: 'link',
  ICON: 'icon',
};

const emptyLayout = {
  contentText: '',
  style: {
    gridArea: tLayout.EMPTY,
  },
};

const statusLayout = {
  iconColor: '',
  contentText: '',
  style: {
    gridArea: tLayout.STATUS,
    justifySelf: 'end'
  },
};

const linkLayout = {
  contentText: '',
  style: {
    gridArea: tLayout.LINK,
  },
  linkTo: {
    name: '',
    params: {},
  },
};

const textLayout = {
  contentText: '',
  style: {
    gridArea: tLayout.TEXT,
  },
};

const textBoxLayout = {
  contentText: '',
  style: {
    gridArea: tLayout.TEXT_BOX,
    justifySelf: 'end'
  },
};
const linkBoxLayout = {
  style: {
    gridArea: tLayout.LINK_BOX,
  },
  linkTo: {
    name: '',
    params: {},
  },
};

const defaultIconStyle = {
  gridArea: tLayout.ICON,
};

const defaultStyleCard = {
  display: 'grid',
  gridTemplateRows: '40px',
  gridTemplateColumns: 'auto 1fr auto',
  gridColumnGap: '24px',
  alignItems: 'center',
  padding: '5px',
};

const defaultStyleCardDisabled = {
  display: 'grid',
  gridTemplateRows: '40px',
  gridTemplateColumns: '40px auto',
  gridTemplateAreas: `"${tLayout.ICON} ${tLayout.EMPTY}"`,
  emptyLayout: { ...emptyLayout },
  padding: '5px',
};

const cardsConfig = {
  slugCard: {
    style: {
      ...defaultStyleCard,
      gridTemplateRows: 'repeat(2, minmax(20px, auto))',
      gridTemplateColumns: '24px 1fr minmax(0, 1fr)',
      gridColumnGap: '24px',
      gridTemplateAreas:`
        "${tLayout.ICON} ${tLayout.TEXT} ${tLayout.LINK_BOX}"
        "${tLayout.ICON} ${tLayout.LINK} ${tLayout.LINK_BOX}"
      `,
    },
    iconClass: 'icon-link',
    iconStyle: { ...defaultIconStyle },
    linkLayout: { ...linkLayout, contentText: '' },
    linkBoxLayout: { ...linkBoxLayout },
    responsive: {
      lg: 24,
      md: 24,
      sm: 24
    }
  },

  feeCard: {
    style: {
      ...defaultStyleCard,
      gridTemplateRows: 'repeat(2, minmax(20px, auto))',
      gridTemplateColumns: '24px 1fr auto',
      gridColumnGap: '24px',
      gridTemplateAreas: `
        "${tLayout.ICON} ${tLayout.TEXT} ${tLayout.TEXT_BOX}"
        "${tLayout.ICON} ${tLayout.LINK} ${tLayout.TEXT_BOX}"
      `,
    },
    iconClass: 'icon-tax',
    iconStyle: { ...defaultIconStyle },
    textLayout: { ...textLayout, contentText: '' },
    textBoxLayout: { ...textBoxLayout, contentText: '' },
    linkLayout: {
      ...linkLayout,
      contentText: i18n('entities.subproperty.finantialOverview.feeCard.linkText'),
      linkTo: {
        name: '',
        params: {},
      },
    },
    responsive: {
      lg: 12,
      md: 24,
      sm: 24
    }
  },

  connectedAccountCard: {
    style: {
      ...defaultStyleCard,
      gridTemplateRows: 'repeat(2, minmax(20px, auto))',
      gridTemplateColumns: '24px 1fr auto',
      gridColumnGap: '24px',
      gridTemplateAreas: `
        "${tLayout.ICON} ${tLayout.TEXT} ${tLayout.STATUS}"
        "${tLayout.ICON} ${tLayout.LINK} ${tLayout.STATUS}"
      `,
    },
    iconClass: 'icon-player',
    iconStyle: { ...defaultIconStyle },
    textLayout: { ...textLayout, contentText: '' },
    statusLayout: {
      ...statusLayout,
      contentText: '',
      iconClass: '',
    },
    responsive: {
      lg: 12,
      md: 24,
      sm: 24
    }
  },
};

export default {
  namespaced: true,

  state: {
    loading: false,
    disabled: false,

    slugCardDisabled: {
      style: { ...defaultStyleCardDisabled },
      emptyLayout: {
        contentText: i18n('entities.subproperty.finantialOverview.slugCard.emptyText'),
      },
      infoLayout: {
        contentText: i18n('entities.subproperty.finantialOverview.slugCard.infoText'),
      },
      responsive: {
        lg: 24,
        md: 24,
        sm: 24
      }
    },
    feeCardDisabled: {
      style: { ...defaultStyleCardDisabled },
      emptyLayout: {
        contentText: '',
      },
      responsive: {
        lg: 12,
        md: 24,
        sm: 24
      }
    },
    connectedAccountCardDisabled: {
      style: { ...defaultStyleCardDisabled },
      emptyLayout: {
        contentText: i18n(
          'entities.subproperty.finantialOverview.connectedAccountCard.emptyText',
        ),
      },
      infoLayout: {
        contentText: i18n(
          'entities.subproperty.finantialOverview.connectedAccountCard.infoText',
        ),
      },
      responsive: {
        lg: 12,
        md: 24,
        sm: 24
      }
    },

    slugCard: {},
    feeCard: {},
    connectedAccountCard: {},

    hasOwqloFeeConfigured: false,
  },

  getters: {
    loading: (state) => state.loading,
    disabled: (state) => state.disabled,
    hasOwqloFeeConfigured: (state) => state.hasOwqloFeeConfigured,
    finantialCardsObject: (state) => {
      return state.loading
        ? {}
        : {
          slug: state.slugCard,
          fee: state.feeCard,
          connectedAccount: state.connectedAccountCard,
        }
    },
  },

  mutations: {
    INIT_CONFIG(
      state,
      { slugCard, feeCard, connectedAccountCard },
    ) {
      state.slugCard = slugCard;
      state.feeCard = feeCard;
      state.connectedAccountCard = connectedAccountCard;
    },

    FETCH_STARTED(state) {
      state.loading = true;
    },

    FETCH_SUCCESS(state) {
      state.loading = false;
    },

    RESET(state) {
      state.loading = false;
      state.slugCard = {};
      state.feeCard = {};
      state.connectedAccountCard = {};
      state.hasOwqloFeeConfigured = false;
    },

    CARD_REPLACE(state, { cardKey, cardKeyToReplace }) {
      _.update(
        state,
        `${cardKey}`,
        () => state[`${cardKeyToReplace}`],
      );
    },

    CARD_INFO(state, { cardKey, contentText }) {
      _.update(
        state,
        `${cardKey}.infoLayout.contentText`,
        () => contentText,
      );
    },

    CARD_ERROR(state, { cardKey, contentText }) {
      _.update(
        state,
        `${cardKey}.errorLayout.contentText`,
        () => contentText,
      );
    },
    
    CARD_ERROR_REMOVE(state, { cardKey }) {
      _.update(state, `${cardKey}.errorLayout`, () => null);
    },

    CARD_TEXT(state, { cardKey, contentText }) {
      _.update(
        state,
        `${cardKey}.textLayout.contentText`,
        () => contentText,
      );
    },

    CARD_ICON(state, { cardKey, iconClass }) {
      _.update(
        state,
        `${cardKey}.iconClass`,
        () => iconClass,
      );
    },

    CARD_TEXT_BOX(state, { cardKey, fees, currency }) {
      _.update(
        state,
        `${cardKey}.textBoxLayout.params`,
        () => { return { fees, currency } },
      );
    },

    CARD_BUTTON(state, { cardKey, linkTo }) {
      _.update(state, `${cardKey}.buttonLayout`, () => {
        return { iconClass: 'icon-pen', linkTo };
      });
    },

    CARD_BUTTON_REMOVE(state, { cardKey }) {
      _.update(
        state,
        `${cardKey}.buttonLayout`,
        () => null,
      );
    },

    CARD_STATUS(
      state,
      { cardKey, iconColor, contentText, enabled },
    ) {
      _.update(state, `${cardKey}.statusLayout.contentText`, () => contentText);
      _.update(state, `${cardKey}.statusLayout.iconColor`, () => iconColor);
      _.update(state, `${cardKey}.statusLayout.enabled`, () => enabled);
    },

    CARD_LINK(
      state,
      { cardKey, linkTo, linkUrl, contentText },
    ) {
      _.update(state, `${cardKey}.linkLayout`, () => {
        return { linkTo, linkUrl, contentText };
      });
    },
    
    CARD_LINK_BOX(
      state,
      { cardKey, linkTo, linkUrl, contentText },
    ) {
      _.update(state, `${cardKey}.linkBoxLayout.contentText`, () => {
        return contentText;
      });

      _.update(state, `${cardKey}.linkBoxLayout.linkTo`, () => {
        return linkTo;
      });
      
      _.update(state, `${cardKey}.linkBoxLayout.linkUrl`, () => {
        return linkUrl;
      });
    },
    
    CARD_LINK_REMOVE(state, { cardKey }) {
      _.update(state, `${cardKey}.linkLayout`, () => null);
    },

    OWQLO_FEE_CONFIGURED(state, payload) {
      state.hasOwqloFeeConfigured = payload;
    },
  },

  actions: {
    doConnectedAccountCardError({ commit }, errorMsg) {
      commit('CARD_ERROR', {
        cardKey: 'connectedAccountCard',
        iconClass: 'icon-warning',
        contentText: errorMsg,
      });
    },

    doConnectedAccountCardErrorRemove({ commit }) {
      commit('CARD_ERROR_REMOVE', {
        cardKey: 'connectedAccountCard',
      });
    },

    doRemoveLinks({ commit }) {
      commit('CARD_BUTTON_REMOVE', { cardKey: 'slugCard' });
      commit('CARD_LINK_REMOVE', { cardKey: 'feeCard' });
    },

    doReset({ commit }) {
      commit('INIT_CONFIG', cardsConfig);
    },

    async doFetch({ commit, dispatch, rootGetters }, { propertyId, subpropertyId }) {
      if (!propertyId || !subpropertyId)  throw `Unknown propertyId: ${propertyId}, subpropertyId: ${subpropertyId}`
      
      try {
        commit('RESET');
        commit('FETCH_STARTED');
        const cards = Object.assign({}, cardsConfig);

        commit('INIT_CONFIG', cards);

        const response = await SubpropertyService.finantialOverview(subpropertyId);
        const {
          slug,
          country,
          owqloFee,
          connectedAccountEnabled,
          defaultCurrency,
        } = response;

        if (slug) {
          const urlName = `${config.registrationSiteUrl}/${slug}`;
          commit('CARD_TEXT', {
            cardKey: 'slugCard',
            contentText: i18n('entities.subproperty.finantialOverview.slugCard.configSlugText'),
          });

          commit('CARD_LINK', {
            cardKey: 'slugCard',
            contentText: i18n('entities.subproperty.finantialOverview.slugCard.linkText'),
            linkTo: {
              name: 'subpropertyCustomizeSettings',
              params: {
                propertyId,
                subpropertyId
              },
            }
          });

          commit('CARD_LINK_BOX', {
            cardKey: 'slugCard',
            contentText: 'url box',
            linkUrl: urlName,
          });

        } else {
          commit('CARD_REPLACE', {
            cardKey: 'slugCard',
            cardKeyToReplace: 'slugCardDisabled',
          });
        }

        if (owqloFee) {
          const { fixedAmount, percentage } = owqloFee;

          const stripeFees = stripePaymentFeesByCountry[country]
          const totalFees = stripeFees.map(fee => ({
            ...fee,
            amount: fee.amount + fixedAmount,
            percentage: fee.percentage + percentage,
          }))

          let iconClass = 'icon-card';

          switch (defaultCurrency) {
            case 'eur': iconClass = 'icon-euro'; break
            case 'cad': iconClass = 'icon-can-dolar'; break
            case 'usd': iconClass = 'icon-dolar'; break
            case 'gbp': iconClass = 'icon-pound'; break
            default:    iconClass = 'icon-card'; break
          }
          
          commit('CARD_ICON', {
            cardKey: 'feeCard',
            iconClass: iconClass,
          });
          commit('CARD_TEXT', {
            cardKey: 'feeCard',
            contentText: i18n('entities.subproperty.finantialOverview.feeCard.owqloFeeText'),
          });
          commit('CARD_LINK', {
            cardKey: 'feeCard',
            contentText: i18n('entities.subproperty.finantialOverview.feeCard.linkText'),
            linkTo: {
              name: 'subpropertyFeeInfo',
              params: {
                id: subpropertyId,
              },
            },
          });
          commit('CARD_TEXT_BOX', {
            cardKey: 'feeCard',
            fees: totalFees,
            currency: defaultCurrency,
          });
          commit('OWQLO_FEE_CONFIGURED', true);
        } else {
          commit('CARD_REPLACE', {
            cardKey: 'feeCard',
            cardKeyToReplace: 'feeCardDisabled',
          });
          commit('OWQLO_FEE_CONFIGURED', false);
        }

        if (connectedAccountEnabled === null) {
          commit('CARD_REPLACE', {
            cardKey: 'connectedAccountCard',
            cardKeyToReplace: 'connectedAccountCardDisabled',
          });
        } else if (connectedAccountEnabled) {
          const connectedAccountText = i18n(
            'entities.subproperty.finantialOverview.connectedAccountCard.connectedAccountText',
          );
          commit('CARD_TEXT', {
            cardKey: 'connectedAccountCard',
            contentText: connectedAccountText,
          });
          
          commit('CARD_LINK', {
            cardKey: 'connectedAccountCard',
            contentText: i18n('entities.subproperty.finantialOverview.feeCard.linkText'),
            linkTo: {
              name: 'finantialSettings',
              params: {
                propertyId,
                subpropertyId,
              },
            },
          });

          commit('CARD_STATUS', {
            enabled: true,
            cardKey: 'connectedAccountCard',
            iconColor: '#75c5c2',
            contentText: i18n(
              'entities.subproperty.finantialOverview.connectedAccountCard.enabled',
            ),
          });

          dispatch('doConnectedAccountCardErrorRemove');
        } else {
          const connectedAccountText = i18n(
            'entities.subproperty.finantialOverview.connectedAccountCard.connectedAccountText',
          );

          const connectedAccountDisabledText = i18n(
            'entities.subproperty.finantialOverview.connectedAccountCard.connectedAccountDisabledText',
          );

          const connectedAccountIncompleteText = i18n(
            'entities.subproperty.finantialOverview.connectedAccountCard.connectedAccountIncompleteText',
          );

          await dispatch('subproperty/requirements/doFetch', {
            propertyId,
            subpropertyId,
            userId: rootGetters['auth/currentUser'].id,
          }, { root: true });

          const areRequirementsFullfiled = rootGetters['subproperty/requirements/areRequirementsFullfiled'];
          const requirementStepsMissing = !areRequirementsFullfiled.all;

          commit('CARD_TEXT', {
            cardKey: 'connectedAccountCard',
            contentText: connectedAccountText,
          });

          commit('CARD_LINK', {
            cardKey: 'connectedAccountCard',
            contentText: i18n('entities.subproperty.finantialOverview.feeCard.linkText'),
            linkTo: {
              name: 'finantialSettings',
              params: {
                propertyId,
                subpropertyId,
              },
            },
          });

          commit('CARD_ERROR', {
            cardKey: 'connectedAccountCard',
            contentText: requirementStepsMissing ? connectedAccountIncompleteText : connectedAccountDisabledText,
          });

          commit('CARD_STATUS', {
            enabled: false,
            cardKey: 'connectedAccountCard',
            iconColor: '#ee7266',
            contentText: i18n(
              'entities.subproperty.finantialOverview.connectedAccountCard.disabled',
            ),
          });
        }

        commit('FETCH_SUCCESS');
      } catch (error) {
        Errors.handle(error);
        commit('RESET');
      }
    },
  },
};
